
  import { computed, defineComponent, onMounted, ref, watch } from 'vue'
  import { useStore } from 'vuex'
  import { Form, Field } from 'vee-validate'
  import {
    CompositionStockAlias,
    OptionStockAlias,
    StockAlias,
    StockGroup
  } from '@/services/api/models'
  import { StockGroupFormData, STOCKTYPE } from '@/types'
  import { Input, Button } from '@up.law/uplaw-ui'
  import FormActions from '@/components/Forms/Fields/ModalActions.vue'
  import { CompanyModel } from '@/services/api/models1'
  import {
    CREATE_COMPANY_STOCK_GROUP,
    EDIT_COMPANY_STOCK_GROUP
  } from '@/store/modules/Organization/models'
  import { TYPE } from 'vue-toastification'
  import { useI18n } from 'vue-i18n'
  import { groupSchema } from '@/utils/schema/group'
  import DropdownStocks from '../../DropDowns/DropdownStocks.vue'
  import ColorPicker from '@/components/ColorPicker/index.vue'
  import { ElPopconfirm } from 'element-plus'
  import { useAcl } from 'vue-simple-acl/src'

  export default defineComponent({
    name: 'UplStockGroup',
    components: {
      Form,
      ElPopconfirm,
      Field,
      Input,
      FormActions,
      Button,
      ColorPicker,
      DropdownStocks
    },

    setup() {
      const store = useStore()
      const { t } = useI18n()
      const acl = useAcl()
      const stockAliasIds = ref<string[]>([])
      const optionAliasIds = ref<string[]>([])
      const compositionAliasIds = ref<string[]>([])
      const newGroup = ref<StockGroupFormData>({
        name: '',
        colorCode: '',
        description: null,
        stocksId: []
      })

      const resetForm = () => {
        newGroup.value = {
          name: '',
          colorCode: '',
          description: null,
          stocksId: []
        }
      }
      const canDo = computed(
        () =>
          acl.role('admin') ||
          (acl.permission('is-manager') && acl.permission('write-stocks'))
      )
      const stocksAliases = computed(
        () => store.getters['organizations/stockAliases']
      )
      const optionStockAliases = computed(
        () => store.getters['organizations/optionStockAliases']
      )

      const compositionStockAlias = computed(
        () => store.getters['organizations/compositionStockAlias']
      )

      const groupedStocks = computed(() => [
        {
          id: 1,
          name: t('companyStockType.SIMPLE'),
          label: t('companyStockType.SIMPLE'),
          value: 'stocks',
          groups: stocksAliases.value.map((x: any) => ({
            name: x.alias,
            label: x.alias,
            id: x.id
          }))
        },
        {
          id: 2,
          name: t('companyStockType.COMPOSED'),
          label: t('companyStockType.COMPOSED'),
          value: 'optionStock',
          groups: optionStockAliases.value.map((x: any) => ({
            name: x.alias,
            label: x.alias,
            id: x.id
          }))
        },
        {
          id: 3,
          name: t('companyStockType.COMBINED'),
          label: t('companyStockType.COMBINED'),
          value: 'compositionStocks',
          groups: compositionStockAlias.value.map((x: any) => ({
            name: x.alias,
            label: x.alias,
            id: x.id
          }))
        }
      ])

      const modalForm = computed(() => store.getters['organizations/form'])

      const group = computed<StockGroup>(
        () => store.getters['organizations/stockGroup']
      )

      const currentCompany = computed<CompanyModel>(
        () => store.getters['organizations/contextCompany']
      )

      const schema = groupSchema

      const initForm = () => {
        store.commit('organizations/SET_CURRENT_STOCK_GROUP', null)
        store.dispatch('organizations/INIT_MODAL_FORM', {
          ...store.getters['organizations/form'],
          modalInit: true,
          initCurrentForm: true
        })
      }

      const stocks = computed<
        (StockAlias | OptionStockAlias | CompositionStockAlias)[]
      >(() => store.getters['organizations/allStocks'])

      const handleForm = (values: StockGroupFormData) => {
        let edition = false
        let action = CREATE_COMPANY_STOCK_GROUP

        values.stocksId?.forEach((id) => {
          const stock = stocks.value.find((x) => x.id === id)
          if (stock && stock?.type === STOCKTYPE.COMPOSITION)
            compositionAliasIds.value.push(stock.id)
          else if (stock && stock?.type === STOCKTYPE.OPTION)
            optionAliasIds.value.push(stock.id)
          else stockAliasIds.value.push(id)
        })
        const data = {
          ...values,
          stockAliasIds: stockAliasIds.value,
          optionAliasIds: optionAliasIds.value,
          compositionAliasIds: compositionAliasIds.value
        }
        delete data.stocksId
        let formData = {
          companyId: currentCompany.value.id,
          data,
          groupId: ''
        }
        if (group.value && group.value.id) {
          edition = true
          formData['groupId'] = group.value.id
          action = EDIT_COMPANY_STOCK_GROUP
        }

        store.dispatch(`organizations/${action}`, {
          ...formData,
          onError: (error = null) => {
            const message =
              error ||
              t(
                `stockGroupBook.apiResponse.${edition ? 'edit' : 'create'}Error`
              )
            store.dispatch('toolbox/displayToast', {
              type: TYPE.ERROR,
              context: t('stockGroupBook.form.title'),
              message
            })
          },
          onSuccess: () => {
            initForm()
            resetForm()
            store.dispatch('toolbox/displayToast', {
              type: TYPE.SUCCESS,
              context: t('stockGroupBook.form.title'),
              message: t(
                `stockGroupBook.apiResponse.${edition ? 'edited' : 'created'}`
              )
            })
          }
        })
      }

      const deleteGroup = () => {
        store.dispatch(`organizations/DELETE_COMPANY_STOCKS_GROUP`, {
          companyId: currentCompany.value.id,
          groupId: group.value.id,
          onSuccess: () => {
            initForm()
            resetForm()
            store.dispatch('toolbox/displayToast', {
              type: TYPE.SUCCESS,
              context: t('stockGroupBook.form.title'),
              message: t(`stockGroupBook.apiResponse.deleted`)
            })
          },
          onError: (error = null) => {
            const message = error || t(`stockGroupBook.apiResponse.deleteError`)
            store.dispatch('toolbox/displayToast', {
              type: TYPE.ERROR,
              context: t('stockGroupBook.form.title'),
              message
            })
          }
        })
      }

      const handleStocksSelection = (data: any) => {
        newGroup.value.stocksId = data.value ?? []
      }

      const handleReset = () => {
        initForm()
      }

      const getStocks = async () => {
        store.dispatch('organizations/GET_ALL_STOCK', {
          companyId: currentCompany.value.id
        })
      }

      const callbackFunc = (stock: any) => {
        return !stock.group
      }

      const toggleModal = (state: boolean) =>
        store.dispatch('ui/toggleModal', { display: state })

      watch(group, (newValue) => {
        if (newValue) {
          const simples = newValue.stockAliases || []
          const options = newValue.optionAliases || []
          const compositions = newValue.compositionAliases || []

          newGroup.value = {
            stocksId:
              [...simples, ...options, ...compositions].map((x) => x.id) || [],
            colorCode: newValue.colorCode || null,
            name: newValue.name,
            description: newValue.description || null
          }
        }
      })

      watch(modalForm, (newValues) => {
        if (newValues.initCurrentForm) resetForm()
      })

      onMounted(() => {
        getStocks()
      })

      return {
        newGroup,
        group,
        canDo,
        schema,
        stocks,
        toggleModal,
        handleForm,
        deleteGroup,
        handleReset,
        handleStocksSelection,
        callbackFunc
      }
    }
  })
